import { type ComponentProps } from 'preact';
import { SvgIcon } from '../Icon/SvgIcon';
import styles from './LinkSelect.module.css';

/* each link has a URL and label */
export interface LinkSelectOption {
  href: string;
  label: string;
}

/* links are organised in labelled optgroups*/
export interface LinkSelectGroup {
  label: string;
  options: LinkSelectOption[];
}

export interface LinkSelectProps extends Omit<
  ComponentProps<'select'>,
  'children' | 'defaultValue' | 'onChange' | 'value'
> {
  groups: LinkSelectGroup[];
  placeholder: string;
  label?: string;
  error?: string;
  onOpenLink?: (href: string) => void;
}

/** A dropdown list of grouped links, that opens links in a new tab */
export function LinkSelect({
  groups,
  placeholder,
  label,
  error,
  disabled = false,
  onOpenLink,
  ...props
}: LinkSelectProps) {
  const availableGroups = groups.filter((group) => group.options.length > 0);
  const isDisabled = disabled || availableGroups.length === 0;

  const handleOpenLink = (href: string) => {
    if (onOpenLink) {
      onOpenLink(href);
      return;
    }

    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={styles.container}>
      {label && <label className={styles.label}>{label}</label>}
      <div className={styles.field}>
        <select
          {...props}
          className={`${styles.select} ${error ? styles.error : ''}`.trim()}
          defaultValue=""
          disabled={isDisabled}
          onChange={(event) => {
            const nextHref = event.currentTarget.value;

            if (nextHref) {
              handleOpenLink(nextHref);
              event.currentTarget.value = '';
            }
          }}
        >
          <option value="">{placeholder}</option>
          {availableGroups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((option) => (
                <option
                  key={`${group.label}-${option.href}`}
                  value={option.href}
                >
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <span className={styles.chevron} aria-hidden>
          <SvgIcon name="chevronDown" size={24} />
        </span>
      </div>
      {error && <span className={styles.errorMessage}>{error}</span>}
    </div>
  );
}
